import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {Animated, View} from 'react-native';
import TransparentOverlay from '@components/TransparentOverlay/TransparentOverlay';
import Text from '@components/Text';
import useStyleUtils from '@hooks/useStyleUtils';
import CONST from '@src/CONST';
import textRef from '@src/types/utils/textRef';
import viewRef from '@src/types/utils/viewRef';
import type {BaseGenericTooltipProps} from './types';

// Props will change frequently.
// On every tooltip hover, we update the position in state which will result in re-rendering.
// We also update the state on layout changes which will be triggered often.
// There will be n number of tooltip components in the page.
// It's good to memoize this one.
function BaseGenericTooltip({
  animation,
  windowWidth,
  xOffset,
  yOffset,
  targetWidth,
  targetHeight,
  shiftHorizontal = 0,
  shiftVertical = 0,
  text,
  numberOfLines,
  maxWidth = 0,
  renderTooltipContent,
  shouldForceRenderingBelow = false,
  wrapperStyle = {},
  anchorAlignment = {
    horizontal: CONST.MODAL.ANCHOR_ORIGIN_HORIZONTAL.CENTER,
    vertical: CONST.MODAL.ANCHOR_ORIGIN_VERTICAL.BOTTOM,
  },
  shouldUseOverlay = false,
  onPressOverlay = () => {},
}: BaseGenericTooltipProps): React.ReactNode {
  // The width of tooltip's inner content. Has to be undefined in the beginning
  // as a width of 0 will cause the content to be rendered of a width of 0,
  // which prevents us from measuring it correctly.
  const [contentMeasuredWidth, setContentMeasuredWidth] = useState<number>();
  // The height of tooltip's wrapper.
  const [wrapperMeasuredHeight, setWrapperMeasuredHeight] = useState<number>();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const rootWrapper = useRef<HTMLDivElement | null>(null);

  const StyleUtils = useStyleUtils();

  useLayoutEffect(() => {
    // Calculate the tooltip width and height before the browser repaints the screen to prevent flicker
    // because of the late update of the width and the height from onLayout.
    setContentMeasuredWidth(contentRef.current?.getBoundingClientRect().width);
    setWrapperMeasuredHeight(
      rootWrapper.current?.getBoundingClientRect().height,
    );
  }, []);

  const {
    animationStyle,
    rootWrapperStyle,
    textStyle,
    pointerWrapperStyle,
    pointerStyle,
  } = useMemo(
    () =>
      StyleUtils.getTooltipStyles({
        tooltip: rootWrapper.current,
        currentSize: animation,
        windowWidth,
        xOffset,
        yOffset,
        tooltipTargetWidth: targetWidth,
        tooltipTargetHeight: targetHeight,
        maxWidth,
        tooltipContentWidth: contentMeasuredWidth,
        tooltipWrapperHeight: wrapperMeasuredHeight,
        manualShiftHorizontal: shiftHorizontal,
        manualShiftVertical: shiftVertical,
        shouldForceRenderingBelow,
        anchorAlignment,
        wrapperStyle,
      }),
    [
      StyleUtils,
      animation,
      windowWidth,
      xOffset,
      yOffset,
      targetWidth,
      targetHeight,
      maxWidth,
      contentMeasuredWidth,
      wrapperMeasuredHeight,
      shiftHorizontal,
      shiftVertical,
      shouldForceRenderingBelow,
      anchorAlignment,
      wrapperStyle,
    ],
  );

  let content;
  if (renderTooltipContent) {
    content = <View ref={viewRef(contentRef)}>{renderTooltipContent()}</View>;
  } else {
    content = (
      <Text numberOfLines={numberOfLines} style={textStyle}>
        <Text style={textStyle} ref={textRef(contentRef)}>
          {text}
        </Text>
      </Text>
    );
  }

  const body = document.querySelector('body');

  if (!body) {
    return null;
  }

  return ReactDOM.createPortal(
    <>
      {shouldUseOverlay && <TransparentOverlay onPress={onPressOverlay} />}
      <Animated.View
        ref={viewRef(rootWrapper)}
        style={[rootWrapperStyle, animationStyle]}>
        {content}
        <View style={pointerWrapperStyle}>
          <View style={pointerStyle} />
        </View>
      </Animated.View>
    </>,
    body,
  );
}

BaseGenericTooltip.displayName = 'BaseGenericTooltip';

export default React.memo(BaseGenericTooltip);
